import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  LinearProgress
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import PaymentIcon from '@mui/icons-material/Payment';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import { useOrganization } from '../../context/OrganizationContext';
import { useOrganizationAccess } from '../../hooks/useOrganizationAccess';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import OrganizationLayout from '../../components/organization/OrganizationLayout';
import styles from '../../styles/OrganizationSettings.module.css';

/**
 * Unified OrganizationSettings Page
 *
 * Consolidated page with tabs for:
 * - Tab 1: Organization Profile (name, industry, description)
 * - Tab 2: Billing & Subscription (subscription details, usage, billing portal)
 * - Tab 3: Integrations (third-party integrations)
 *
 * Access: Owner and Admin can view all tabs, Owner can manage billing
 */
function OrganizationSettings() {
  const { currentOrganization, userRole, updateOrganization } = useOrganization();
  const { hasWriteAccess, getDisabledMessage } = useOrganizationAccess();
  const { getAccessTokenSilently } = useAuth0();
  const location = useLocation();

  // Tab state - read from URL query parameter
  const getInitialTab = () => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam === 'billing') return 1;
    if (tabParam === 'integrations') return 2;
    return 0;
  };

  const [currentTab, setCurrentTab] = useState(getInitialTab());

  // Organization Profile state
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    organizationName: currentOrganization?.organizationName || '',
    organizationIdentifier: currentOrganization?.organizationIdentifier || '',
    industry: currentOrganization?.industry || '',
    description: currentOrganization?.description || ''
  });
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Billing & Subscription state
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [subscriptionError, setSubscriptionError] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);

  // Permissions
  const canEdit = hasWriteAccess && (userRole === 'owner' || userRole === 'admin');
  const isOwner = userRole === 'owner';

  // Update tab when URL query parameter changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam === 'billing') {
      setCurrentTab(1);
    } else if (tabParam === 'integrations') {
      setCurrentTab(2);
    } else {
      setCurrentTab(0);
    }
  }, [location.search]);

  // Update form data when organization changes
  useEffect(() => {
    if (currentOrganization) {
      setFormData({
        organizationName: currentOrganization.organizationName || '',
        organizationIdentifier: currentOrganization?.organizationIdentifier || '',
        industry: currentOrganization.industry || '',
        description: currentOrganization.description || ''
      });
    }
  }, [currentOrganization]);

  // Fetch subscription details when on billing tab
  useEffect(() => {
    if (currentTab === 1 && currentOrganization?.organizationUuid && !subscriptionDetails) {
      fetchSubscriptionDetails();
    }
  }, [currentTab, currentOrganization]);

  const fetchSubscriptionDetails = async () => {
    try {
      setLoadingSubscription(true);
      const token = await getAccessTokenSilently();

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/organization/subscription/details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          organizationId: currentOrganization?.organizationUuid
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch subscription details');
      }

      const data = await response.json();
      setSubscriptionDetails(data);
    } catch (err) {
      setSubscriptionError('Failed to load subscription details. Please try again.');
    } finally {
      setLoadingSubscription(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
    setSaveSuccess(false);
    setSaveError('');
  };

  const handleEdit = () => {
    setIsEditing(true);
    setSaveSuccess(false);
    setSaveError('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset form data to original values
    setFormData({
      organizationName: currentOrganization?.organizationName || '',
      organizationIdentifier: currentOrganization?.organizationIdentifier || '',
      industry: currentOrganization?.industry || '',
      description: currentOrganization?.description || ''
    });
    setSaveSuccess(false);
    setSaveError('');
  };

  const handleSave = async () => {
    try {
      const token = await getAccessTokenSilently();

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/organization/${currentOrganization?.organizationUuid}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          organizationName: formData.organizationName,
          industry: formData.industry,
          description: formData.description
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update organization settings');
      }

      const result = await response.json();

      // Update organization context
      if (updateOrganization && result.organization) {
        updateOrganization(result.organization);
      }

      setSaveSuccess(true);
      setIsEditing(false);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      setSaveError('Failed to save settings. Please try again.');
    }
  };

  // Open Stripe Customer Portal
  const handleManageBilling = async () => {
    if (!currentOrganization?.subscription?.stripeCustomerId) {
      setSubscriptionError('No Stripe customer ID found. Please contact support.');
      return;
    }

    try {
      setPortalLoading(true);
      const token = await getAccessTokenSilently();

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/organization/subscription/portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          stripeCustomerId: currentOrganization.subscription.stripeCustomerId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create portal session');
      }

      const data = await response.json();
      window.open(data.url, '_blank');
    } catch (err) {
      setSubscriptionError('Failed to open billing portal. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  // Get status chip configuration
  const getStatusChip = (status) => {
    const statusConfig = {
      active: { label: 'Active', color: 'success', icon: <CheckCircleIcon /> },
      trialing: { label: 'Trial', color: 'info', icon: <CheckCircleIcon /> },
      past_due: { label: 'Past Due', color: 'warning', icon: <WarningIcon /> },
      canceled: { label: 'Canceled', color: 'error', icon: <ErrorIcon /> },
      incomplete: { label: 'Incomplete', color: 'warning', icon: <WarningIcon /> },
      incomplete_expired: { label: 'Expired', color: 'error', icon: <ErrorIcon /> },
      unpaid: { label: 'Unpaid', color: 'error', icon: <ErrorIcon /> }
    };

    const config = statusConfig[status] || { label: status, color: 'default' };

    return (
      <Chip
        label={config.label}
        color={config.color}
        icon={config.icon}
        size="medium"
        sx={{ fontWeight: 600 }}
      />
    );
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format currency
  const formatCurrency = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100);
  };

  // Calculate usage percentage
  const getUsagePercentage = (used, max) => {
    if (max === 0) return 0;
    return Math.min((used / max) * 100, 100);
  };

  // Get usage color
  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    return 'primary';
  };

  const industries = [
    'Commercial',
    'Real Estate/Construction',
    'Agriculture',
    'Public Safety',
    'Inspection',
    'Media/Film',
    'Surveying/Mapping',
    'Environmental',
    'Other'
  ];

  return (
    <OrganizationLayout>
      <div className={styles.container}>
        {/* Page Header */}
        <Box className={styles.header}>
          <Box display="flex" alignItems="center" gap={2}>
            <BusinessIcon sx={{ fontSize: 40, color: '#1976d2' }} />
            <Box>
              <Typography variant="h4" component="h1">
                Organization Settings
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                Manage your organization profile, billing, and subscription
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={currentTab} onChange={handleTabChange} aria-label="organization settings tabs">
            <Tab label="Organization Profile" />
            <Tab label="Billing & Subscription" />
            <Tab label="Integrations" />
          </Tabs>
        </Box>

        {/* Success/Error Messages */}
        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Organization settings saved successfully!
          </Alert>
        )}
        {saveError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {saveError}
          </Alert>
        )}
        {subscriptionError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubscriptionError('')}>
            {subscriptionError}
          </Alert>
        )}
        {!canEdit && hasWriteAccess && currentTab === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Only organization owners and admins can edit settings.
          </Alert>
        )}
        {!isOwner && currentTab === 1 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You have view-only access to billing information. Only the organization Owner can manage billing settings.
          </Alert>
        )}

        {/* Tab Panel 0: Organization Profile */}
        {currentTab === 0 && (
          <Grid container spacing={3}>
            {/* Organization Profile */}
            <Grid item xs={12} md={8}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="h6" component="h2">
                      Organization Profile
                    </Typography>
                    {!isEditing && canEdit && (
                      <Button
                        variant="outlined"
                        onClick={handleEdit}
                        disabled={!hasWriteAccess}
                        title={!hasWriteAccess ? getDisabledMessage() : ''}
                      >
                        Edit
                      </Button>
                    )}
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Organization Name"
                        value={formData.organizationName}
                        onChange={handleChange('organizationName')}
                        disabled={!isEditing}
                        required
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Organization Identifier"
                        value={formData.organizationIdentifier}
                        disabled={true}
                        helperText="Organization URL identifier cannot be changed"
                        InputProps={{
                          startAdornment: <Typography color="textSecondary" sx={{ mr: 1 }}></Typography>
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth disabled={!isEditing}>
                        <InputLabel>Industry</InputLabel>
                        <Select
                          value={formData.industry}
                          onChange={handleChange('industry')}
                          label="Industry"
                        >
                          {industries.map((ind) => (
                            <MenuItem key={ind} value={ind}>
                              {ind}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        value={formData.description}
                        onChange={handleChange('description')}
                        disabled={!isEditing}
                        multiline
                        rows={3}
                        placeholder="Describe your organization's activities and purpose"
                      />
                    </Grid>
                  </Grid>

                  {isEditing && (
                    <Box display="flex" gap={2} mt={3}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                      >
                        Save Changes
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={handleCancel}
                      >
                        Cancel
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Quick Info Sidebar */}
            <Grid item xs={12} md={4}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" component="h2" gutterBottom>
                    Quick Info
                  </Typography>
                  <Divider sx={{ my: 2 }} />

                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Your Role
                    </Typography>
                    <Chip
                      label={userRole?.toUpperCase() || 'Member'}
                      color="primary"
                      size="small"
                      variant="outlined"
                    />
                  </Box>

                  <Box mb={2}>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      Team Members
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {currentOrganization?.memberCount || 1}
                    </Typography>
                  </Box>

                  <Button fullWidth variant="outlined" href="/settings/team">
                    Manage Team
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Tab Panel 1: Billing & Subscription */}
        {currentTab === 1 && (
          <>
            {loadingSubscription ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={3}>
                {/* Subscription Overview Card */}
                <Grid item xs={12} md={8}>
                  <Card elevation={2}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Current Subscription
                        </Typography>
                        {isOwner && (
                          <Button
                            variant="contained"
                            startIcon={<OpenInNewIcon />}
                            onClick={handleManageBilling}
                            disabled={portalLoading}
                          >
                            {portalLoading ? 'Opening...' : 'Manage Billing'}
                          </Button>
                        )}
                      </Box>

                      <Divider sx={{ mb: 3 }} />

                      {/* Subscription Details Table */}
                      <TableContainer>
                        <Table>
                          <TableBody>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, width: '40%' }}>Plan</TableCell>
                              <TableCell>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {subscriptionDetails?.planName || currentOrganization?.subscription?.planName || 'N/A'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                              <TableCell>
                                {getStatusChip(subscriptionDetails?.status || currentOrganization?.subscription?.status || 'unknown')}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Billing Amount</TableCell>
                              <TableCell>
                                <Typography variant="body1">
                                  {formatCurrency(
                                    subscriptionDetails?.amount || currentOrganization?.subscription?.amount || 0,
                                    subscriptionDetails?.currency || currentOrganization?.subscription?.currency || 'usd'
                                  )}
                                  {' / '}
                                  {subscriptionDetails?.interval || currentOrganization?.subscription?.interval || 'month'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                            {subscriptionDetails?.trialEnd && new Date(subscriptionDetails.trialEnd) > new Date() && (
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Trial Ends</TableCell>
                                <TableCell>
                                  <Typography variant="body1" color="primary" sx={{ fontWeight: 500 }}>
                                    {formatDate(subscriptionDetails.trialEnd)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )}
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Current Period</TableCell>
                              <TableCell>
                                {formatDate(subscriptionDetails?.currentPeriodStart || currentOrganization?.subscription?.currentPeriodStart)}
                                {' - '}
                                {formatDate(subscriptionDetails?.currentPeriodEnd || currentOrganization?.subscription?.currentPeriodEnd)}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Next Billing Date</TableCell>
                              <TableCell>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {formatDate(subscriptionDetails?.currentPeriodEnd || currentOrganization?.subscription?.currentPeriodEnd)}
                                </Typography>
                              </TableCell>
                            </TableRow>
                            {subscriptionDetails?.cancelAtPeriodEnd && (
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Cancellation</TableCell>
                                <TableCell>
                                  <Alert severity="warning" sx={{ py: 0 }}>
                                    Subscription will cancel on {formatDate(subscriptionDetails.currentPeriodEnd)}
                                  </Alert>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Plan Limits Card */}
                <Grid item xs={12} md={4}>
                  <Card elevation={2}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Plan Limits
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      {/* Team Members */}
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="textSecondary">
                            Team Members
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {currentOrganization?.memberCount || 0} / {currentOrganization?.subscription?.limits?.maxUsers || 0}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={getUsagePercentage(
                            currentOrganization?.memberCount || 0,
                            currentOrganization?.subscription?.limits?.maxUsers || 1
                          )}
                          color={getUsageColor(
                            getUsagePercentage(
                              currentOrganization?.memberCount || 0,
                              currentOrganization?.subscription?.limits?.maxUsers || 1
                            )
                          )}
                        />
                      </Box>

                      {/* Drones */}
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="textSecondary">
                            Drones
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {currentOrganization?.droneCount || 0} / {currentOrganization?.subscription?.limits?.maxDrones || 0}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={getUsagePercentage(
                            currentOrganization?.droneCount || 0,
                            currentOrganization?.subscription?.limits?.maxDrones || 1
                          )}
                          color={getUsageColor(
                            getUsagePercentage(
                              currentOrganization?.droneCount || 0,
                              currentOrganization?.subscription?.limits?.maxDrones || 1
                            )
                          )}
                        />
                      </Box>

                      {/* Storage */}
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="textSecondary">
                            Storage
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {currentOrganization?.subscription?.limits?.storageGB || 0} GB
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>

                  {/* Need More? Card */}
                  {isOwner && (
                    <Card elevation={2} sx={{ mt: 2, backgroundColor: '#f5f5f5' }}>
                      <CardContent>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Need More?
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                          Upgrade your plan to get more team members, drones, and storage.
                        </Typography>
                        <Button
                          variant="outlined"
                          fullWidth
                          onClick={handleManageBilling}
                          disabled={portalLoading}
                        >
                          View Plans
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </Grid>

                {/* Billing Portal Info */}
                {isOwner && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      <Typography variant="body2">
                        <strong>Manage Billing</strong> opens the Stripe Customer Portal where you can:
                      </Typography>
                      <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                        <li>Update payment methods</li>
                        <li>View billing history and invoices</li>
                        <li>Change or cancel your subscription</li>
                        <li>Update billing information</li>
                      </Box>
                    </Alert>
                  </Grid>
                )}
              </Grid>
            )}
          </>
        )}

        {/* Tab Panel 2: Integrations */}
        {currentTab === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <IntegrationInstructionsIcon sx={{ fontSize: 40, color: '#1976d2' }} />
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Third-Party Integrations
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Connect your organization with external services
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                      Custom Integration Requests
                    </Typography>
                    <Typography variant="body2">
                      Need a custom integration for your organization? Contact our support team at{' '}
                      <a href="mailto:support@flyzone.io" style={{ color: '#1976d2', textDecoration: 'none', fontWeight: 500 }}>
                        support@flyzone.io
                      </a>
                      {' '}and we'll help you get connected.
                    </Typography>
                  </Alert>

                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <IntegrationInstructionsIcon sx={{ fontSize: 80, color: '#bdbdbd', mb: 2 }} />
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      Coming Soon
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Integration features are currently in development. Stay tuned for updates!
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Need Help?
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Our team is here to assist with integration needs and custom solutions.
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    href="mailto:support@flyzone.io"
                    sx={{ mb: 1 }}
                  >
                    Contact Support
                  </Button>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                    support@flyzone.io
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </div>
    </OrganizationLayout>
  );
}

export default OrganizationSettings;
